// «Estado de la empresa» (shared/API.md §43): the company time clock as a status, beside — never
// instead of — the personal one. Pure: decoding Core's answer, the avatar ring, the status line, the
// day bar, the Registro card's return times and «Mi equipo hoy». Every "HH:MM" from Core is the wall
// clock of `feed.tz` (Mexico), so "now" is read IN that zone, never the device's (people travel).
// Twins: ios/Sources/Models/Attendance.swift, android …/models/Attendance.kt,
// windows SatoriChat.Core/Logic/Attendance.cs — same names, same rules, same tests.
import { tf, t } from '../i18n/i18n';

export type AttendanceState = 'none' | 'working' | 'lunch' | 'leave' | 'closed';
export type AttendanceRing = 'working' | 'lunch' | 'leave' | 'closed' | 'none';
export type AttendanceCommand = 'ci' | 'co' | 'ci-meal' | 'co-meal' | 'ci-ext' | 'co-ext';
export type LunchChoice = 'oneHour' | 'twoHours' | 'other';

export interface AttendanceEvent {
  /** Minutes since midnight in `tz`. */
  at: number;
  type: number;
  until: number | null;
}

export interface AttendancePerson {
  name: string;
  area: string | null;
  areaName: string | null;
  state: AttendanceState;
  since: number | null;
  until: number | null;
  dayStart: number | null;
  dayEnd: number | null;
  events: AttendanceEvent[];
  /** Inside the reader's «Mi equipo hoy» scope (shared/API.md §43.1 `team_scope`). Old Core: true. */
  team: boolean;
}

/** Who «Mi equipo hoy» lists (2026-10-26): `all` Core admin (área toggle), `org` the reader's
 *  org-chart team, `none` no panel; `legacy` = a Core that does not send it ⇒ the old behaviour. */
export type AttendanceTeamScope = 'all' | 'org' | 'none' | 'legacy';

export interface AttendanceMe extends AttendancePerson {
  actions: AttendanceCommand[];
}

export interface AttendanceFeed {
  date: string;
  tz: string;
  workday: boolean;
  lunchMinutes: number;
  me: AttendanceMe | null;
  people: Record<string, AttendancePerson>;
  teamScope: AttendanceTeamScope;
}

export interface AttendanceNow {
  date: string;
  minutes: number;
}

export interface TeamRow {
  userId: string;
  name: string;
  state: AttendanceState;
  line: string;
}

export interface TeamView {
  counts: Record<AttendanceState, number>;
  attention: TeamRow[];
  away: TeamRow[];
  working: TeamRow[];
}

export const ATTENDANCE_PATH = '/api/chat/attendance';
export const ATTENDANCE_FEED_KEY = 'attendance_feed';
export const ATTENDANCE_POLL_MS = 60_000;
/** «Sin registro» is only drawn from this time on (09:30): before it nobody is late. */
export const NONE_RING_FROM = 9 * 60 + 30;
/** The profile's day bar spans 07:00–20:00. */
/** The own chip: one small mark per state beside the dot, never a timer (user, 2026-10-26). */
export const CHIP_WORKING = '✓';
export const CHIP_LUNCH = '🍔';
export const CHIP_LEAVE = '🚪';
export const CHIP_CLOSED = '🏠';
export const DAY_BAR_FROM = 7 * 60;
export const DAY_BAR_TO = 20 * 60;
const STATES: AttendanceState[] = ['none', 'working', 'lunch', 'leave', 'closed'];
const COMMANDS: AttendanceCommand[] = ['ci', 'co', 'ci-meal', 'co-meal', 'ci-ext', 'co-ext'];
/** The state each registry type leaves the person in (Core's AttendanceStatusService). */
const STATE_AFTER: Record<number, AttendanceState> = { 1: 'working', 2: 'closed', 3: 'working', 4: 'lunch', 5: 'working', 6: 'leave' };

/** "08:41" → 521; anything else → null. */
function minutesOf(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : null;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function decodePerson(raw: unknown): AttendancePerson | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const state = STATES.includes(o.state as AttendanceState) ? (o.state as AttendanceState) : null;
  if (!state) return null;
  const events: AttendanceEvent[] = [];
  if (Array.isArray(o.events)) {
    for (const e of o.events) {
      if (!e || typeof e !== 'object') continue;
      const ev = e as Record<string, unknown>;
      const at = minutesOf(ev.t);
      const type = typeof ev.type === 'number' ? ev.type : NaN;
      if (at === null || !(type >= 1 && type <= 6)) continue;
      events.push({ at, type, until: minutesOf(ev.until) });
    }
  }
  return {
    name: typeof o.name === 'string' ? o.name : '',
    area: str(o.area),
    areaName: str(o.area_name),
    state,
    since: minutesOf(o.since),
    until: minutesOf(o.until),
    dayStart: minutesOf(o.day_start),
    dayEnd: minutesOf(o.day_end),
    events,
    team: o.team !== false,
  };
}

export const Attendance = {
  /** Core's answer (object or JSON text); null when it is not one. */
  decode(raw: unknown): AttendanceFeed | null {
    let value = raw;
    if (typeof raw === 'string') {
      try {
        value = JSON.parse(raw);
      } catch {
        return null;
      }
    }
    if (!value || typeof value !== 'object') return null;
    const o = value as Record<string, unknown>;
    const date = str(o.date);
    if (!date) return null;
    let me: AttendanceMe | null = null;
    const mePerson = decodePerson(o.me);
    if (mePerson) {
      const acts = Array.isArray((o.me as Record<string, unknown>).actions) ? ((o.me as Record<string, unknown>).actions as unknown[]) : [];
      me = { ...mePerson, actions: acts.filter((a): a is AttendanceCommand => COMMANDS.includes(a as AttendanceCommand)) };
    }
    const people: Record<string, AttendancePerson> = {};
    if (o.people && typeof o.people === 'object') {
      for (const [id, p] of Object.entries(o.people as Record<string, unknown>)) {
        const person = decodePerson(p);
        if (id && person) people[id] = person;
      }
    }
    return {
      date,
      tz: str(o.tz) ?? 'America/Mexico_City',
      workday: o.workday === true,
      lunchMinutes: typeof o.lunch_minutes === 'number' && o.lunch_minutes > 0 ? o.lunch_minutes : 60,
      me,
      people,
      teamScope: o.team_scope === 'all' || o.team_scope === 'org' || o.team_scope === 'none' ? o.team_scope : 'legacy',
    };
  },

  /** The «Mi equipo hoy» entry is offered (no `none` scope). */
  offersTeam(feed: AttendanceFeed): boolean {
    return feed.teamScope !== 'none';
  },

  /** The wall clock of `tz` at `instant`. */
  now(tz: string, instant: Date): AttendanceNow {
    let parts: Record<string, string>;
    try {
      parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
          .formatToParts(instant)
          .map((p) => [p.type, p.value]),
      );
    } catch {
      parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
          .formatToParts(instant)
          .map((p) => [p.type, p.value]),
      );
    }
    return { date: `${parts.year}-${parts.month}-${parts.day}`, minutes: (Number(parts.hour) % 24) * 60 + Number(parts.minute) };
  },

  /** A persisted answer only speaks for its own day. */
  isCurrent(feed: AttendanceFeed | null, now: AttendanceNow): feed is AttendanceFeed {
    return !!feed && feed.date === now.date;
  },

  /** The avatar ring; null = no ring (working, not a workday, unknown person, before 09:30 for «none»). */
  ring(person: AttendancePerson | null | undefined, workday: boolean, nowMinutes: number): AttendanceRing | null {
    if (!person || !workday) return null;
    switch (person.state) {
      case 'working': // a green hollow ring (user, 2026-10-26)
      case 'lunch':
      case 'leave':
      case 'closed':
        return person.state;
      case 'none':
        return nowMinutes >= NONE_RING_FROM ? 'none' : null;
      default:
        return null;
    }
  },

  overdue(person: AttendancePerson, nowMinutes: number): boolean {
    return (person.state === 'lunch' || person.state === 'leave') && person.until !== null && nowMinutes > person.until;
  },

  /** "8:41" (no leading zero, like the mockups and the apps' clocks). */
  hm(minutes: number): string {
    const m = ((minutes % 1440) + 1440) % 1440;
    return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
  },

  /** "3 h 12 min" / "45 min". */
  duration(minutes: number): string {
    const m = Math.max(0, Math.round(minutes));
    return m >= 60 ? tf('attendance.duration.hm', Math.floor(m / 60), m % 60) : tf('attendance.duration.m', m);
  },

  /** The one-line status. `elapsed` (the reader's own chip): the same small mark as the phone chip — never a running timer. */
  line(person: AttendancePerson, nowMinutes: number, elapsed = false): string {
    if (elapsed) return Attendance.short(person, nowMinutes);
    const at = (m: number | null) => (m === null ? '' : Attendance.hm(m));
    switch (person.state) {
      case 'working':
        return tf('attendance.line.workingSince', at(person.dayStart ?? person.since));
      case 'lunch':
        if (person.until === null) return tf('attendance.line.lunchSince', at(person.since));
        return tf(Attendance.overdue(person, nowMinutes) ? 'attendance.line.lunchLate' : 'attendance.line.lunchBack', at(person.until));
      case 'leave':
        if (person.until === null) return tf('attendance.line.leaveSince', at(person.since));
        return tf(Attendance.overdue(person, nowMinutes) ? 'attendance.line.leaveLate' : 'attendance.line.leaveBack', at(person.until));
      case 'closed':
        return tf('attendance.line.closed', at(person.dayEnd ?? person.since));
      default:
        return t('attendance.line.none');
    }
  },

  /** The phone header's chip: one small mark per state («✓», «🍔», «🚪», «🏠»), never a timer. */
  short(person: AttendancePerson, nowMinutes: number): string {
    switch (person.state) {
      case 'working':
        return CHIP_WORKING;
      case 'lunch':
        return CHIP_LUNCH;
      case 'leave':
        return CHIP_LEAVE;
      case 'closed':
        return CHIP_CLOSED;
      default:
        return t('attendance.state.none');
    }
  },

  /** An event's label in the profile: «Salida a comida (regreso 14:52)». */
  eventLabel(event: AttendanceEvent): string {
    const base = t(`attendance.event.${event.type}`);
    return event.until !== null ? `${base} (${tf('attendance.event.until', Attendance.hm(event.until))})` : base;
  },

  /** The day bar: one segment per stretch in a state, the open one running to `nowMinutes`. */
  segments(person: AttendancePerson, nowMinutes: number): Array<{ from: number; to: number; state: AttendanceState }> {
    const events = [...person.events].sort((a, b) => a.at - b.at);
    const out: Array<{ from: number; to: number; state: AttendanceState }> = [];
    for (let i = 0; i < events.length; i++) {
      const state = STATE_AFTER[events[i].type];
      if (!state || state === 'closed') continue;
      const to = i + 1 < events.length ? events[i + 1].at : Math.max(events[i].at, nowMinutes);
      const last = out[out.length - 1];
      if (last && last.state === state && last.to === events[i].at) last.to = to;
      else if (to > events[i].at) out.push({ from: events[i].at, to, state });
    }
    return out;
  },

  /** Where a segment sits on the 07:00–20:00 bar, as fractions 0…1. */
  barSpan(from: number, to: number): { left: number; width: number } {
    const span = DAY_BAR_TO - DAY_BAR_FROM;
    const a = Math.min(Math.max(from, DAY_BAR_FROM), DAY_BAR_TO);
    const b = Math.min(Math.max(to, DAY_BAR_FROM), DAY_BAR_TO);
    return { left: (a - DAY_BAR_FROM) / span, width: Math.max(0, b - a) / span };
  },

  /** The lunch return the card sends: +1 h / +2 h / the picked time. Null = the picked time already passed. */
  lunchReturn(choice: LunchChoice, nowMinutes: number, lunchMinutes: number, other: number | null): number | null {
    if (choice === 'oneHour') return Math.min(nowMinutes + lunchMinutes, 1439);
    if (choice === 'twoHours') return Math.min(nowMinutes + 120, 1439);
    return other !== null && other > nowMinutes ? other : null;
  },

  /** The salida extraordinaria's first estimate: the next half hour, plus 30 minutes. */
  defaultLeaveReturn(nowMinutes: number): number {
    return Math.min(Math.ceil((nowMinutes + 1) / 30) * 30 + 30, 1439);
  },

  /** −/+ 30 min, never earlier than 15 minutes from now nor past 23:59. */
  stepLeaveReturn(current: number, delta: number, nowMinutes: number): number {
    return Math.min(Math.max(current + delta, nowMinutes + 15), 1439);
  },

  /** "HH:MM" for the request body. */
  wire(minutes: number): string {
    const m = Math.min(Math.max(Math.round(minutes), 0), 1439);
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  },

  /** «Mi equipo hoy»: `area` null = the whole company. Each list sorted by name. */
  team(feed: AttendanceFeed, area: string | null, nowMinutes: number): TeamView {
    const counts: Record<AttendanceState, number> = { none: 0, working: 0, lunch: 0, leave: 0, closed: 0 };
    const attention: TeamRow[] = [];
    const away: TeamRow[] = [];
    const working: TeamRow[] = [];
    for (const [userId, p] of Object.entries(feed.people)) {
      if (feed.teamScope === 'none') continue;
      if (feed.teamScope === 'org' ? !p.team : area !== null && p.area !== area) continue;
      counts[p.state]++;
      const row: TeamRow = { userId, name: p.name, state: p.state, line: Attendance.line(p, nowMinutes) };
      if ((p.state === 'none' && nowMinutes >= NONE_RING_FROM) || Attendance.overdue(p, nowMinutes)) attention.push(row);
      else if (p.state === 'lunch' || p.state === 'leave' || p.state === 'closed') away.push(row);
      else if (p.state === 'working') working.push(row);
    }
    const byName = (a: TeamRow, b: TeamRow) => a.name.localeCompare(b.name, 'es');
    return { counts, attention: attention.sort(byName), away: away.sort(byName), working: working.sort(byName) };
  },
};
